import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeIn } from './FadeIn';
import { Sparkles, Heart, Crown, ChevronLeft, ChevronRight, X } from 'lucide-react';
interface IsashyExperienceProps {
  lang?: 'en' | 'es';
}
const galleryImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/bLjSCNwE88iJ4SBvok5bLr/imgaen_de_unas_1.jpg',
  alt: 'Nail art design 1'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/av43M5vjsktBXUhQdgPcVn/imagen_de_una_2.jpg',
  alt: 'Nail art design 2'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/2vYJJqjL827BnRdVWj8w1e/imagen_de_unas_3.jpg',
  alt: 'Nail art design 3'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/9xTVeogcFEbXAmgZXqYBew/image_de_una_4.jpg',
  alt: 'Nail art design 4'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/pD68QkA9VHLgpacb7H4BVA/imagen_de_unas_pies_5.jpg',
  alt: 'Pedicure design 5'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/64BQT9SpAznGTbSmtEDAhb/imager_de_unas_6.jpg',
  alt: 'Nail art design 6'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/mv2onckVjwhtCxkRCefKHG/imagen_de_unas_7.jpg',
  alt: 'Nail art design 7'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/nSnwkZq3oxGbSoEoRP8Awa/imagen_de_unas_8.jpg',
  alt: 'Nail art design 8'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/venMxNH758xGEtniNJjwzT/imagen_e_unas_9.jpg',
  alt: 'Nail art design 9'
}];
const t = {
  en: {
    label: 'The Unique Mechanism',
    title: 'The ISASHY Experience',
    p1: "Most women don't just want a service. They want to walk in carrying the weight of their week — and walk out feeling like the most beautiful, powerful version of themselves.",
    p2: "The real reason you've been disappointed before? You've been going to places that treat beauty like a transaction.",
    highlight: 'At ISASHY Beauty Center, every visit is built around one philosophy: You are the priority. Your beauty. Your confidence. Your transformation.',
    p3: 'We call it The ISASHY Experience — where skilled hands, premium products, and genuine care combine to deliver results that make you stop and stare at your own reflection.',
    pillar1: 'Skilled Hands',
    pillar2: 'Premium Products',
    pillar3: 'Genuine Care',
    gallery: 'Our Work'
  },
  es: {
    label: 'El Mecanismo Único',
    title: 'La Experiencia ISASHY',
    p1: 'La mayoría de las mujeres no solo quieren un servicio. Quieren entrar cargando el peso de su semana — y salir sintiéndose como la versión más bella y poderosa de sí mismas.',
    p2: '¿La verdadera razón por la que has quedado decepcionada antes? Has ido a lugares que tratan la belleza como una transacción.',
    highlight: 'En ISASHY Beauty Center, cada visita está construida alrededor de una filosofía: Tú eres la prioridad. Tu belleza. Tu confianza. Tu transformación.',
    p3: 'Lo llamamos La Experiencia ISASHY — donde manos expertas, productos premium y cuidado genuino se combinan para entregar resultados que te hacen detenerte y admirar tu propio reflejo.',
    pillar1: 'Manos Expertas',
    pillar2: 'Productos Premium',
    pillar3: 'Cuidado Genuino',
    gallery: 'Nuestro Trabajo'
  }
};
export function IsashyExperience({
  lang = 'en'
}: IsashyExperienceProps) {
  const text = t[lang];
  const [currentSlide, setCurrentSlide] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % galleryImages.length);
  }, []);
  // Auto-rotate every 3 seconds
  useEffect(() => {
    if (paused || lightboxIndex !== null) return;
    const timer = setInterval(nextSlide, 3000);
    return () => clearInterval(timer);
  }, [paused, lightboxIndex, nextSlide]);
  function prevSlide() {
    setCurrentSlide(prev => (prev - 1 + galleryImages.length) % galleryImages.length);
  }
  function openLightbox(index: number) {
    setLightboxIndex(index);
  }
  function closeLightbox() {
    setLightboxIndex(null);
  }
  function lightboxNext() {
    setLightboxIndex(prev => prev !== null ? (prev + 1) % galleryImages.length : null);
  }
  function lightboxPrev() {
    setLightboxIndex(prev => prev !== null ? (prev - 1 + galleryImages.length) % galleryImages.length : null);
  }
  return <section className="py-24 md:py-32 bg-brand-dark text-brand-cream overflow-hidden" data-id="element-143">
      <div className="max-w-7xl mx-auto px-6" data-id="element-144">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-145">
          {/* Gallery Carousel */}
          <FadeIn direction="right" data-id="element-146">
            <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-147">
              <span className="text-brand-gold tracking-[0.2em] text-xs font-medium uppercase mb-4 block text-center" data-id="element-148">
                {text.gallery}
              </span>

              {/* Main carousel image */}
              <div className="relative aspect-square max-h-[500px] overflow-hidden bg-brand-dark cursor-pointer group" onClick={() => openLightbox(currentSlide)} data-id="element-149">
                <AnimatePresence mode="wait" data-id="element-150">
                  <motion.img key={currentSlide} src={galleryImages[currentSlide].src} alt={galleryImages[currentSlide].alt} initial={{
                  opacity: 0,
                  scale: 1.05
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.95
                }} transition={{
                  duration: 0.4,
                  ease: [0.22, 1, 0.36, 1]
                }} className="w-full h-full object-cover" data-id="element-151" />
                </AnimatePresence>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center" data-id="element-152">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-sm font-sans tracking-wider" data-id="element-153">
                    {lang === 'es' ? 'Click para ampliar' : 'Click to enlarge'}
                  </span>
                </div>
              </div>

              {/* Carousel controls */}
              <div className="flex items-center justify-between mt-4" data-id="element-154">
                <button onClick={prevSlide} className="w-10 h-10 flex items-center justify-center border border-brand-cream/15 hover:border-brand-gold hover:text-brand-gold transition-colors" data-id="element-155">
                  <ChevronLeft className="w-5 h-5" data-id="element-156" />
                </button>

                {/* Thumbnail marquee */}
                <div className="overflow-hidden max-w-[240px] md:max-w-[300px]" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-157">
                  <motion.div className="flex items-center gap-2" animate={{
                  x: ['0%', '-50%']
                }} transition={{
                  x: {
                    duration: 15,
                    repeat: Infinity,
                    ease: 'linear',
                    repeatType: 'loop'
                  }
                }} style={{
                  animationPlayState: paused ? 'paused' : 'running'
                }} data-id="element-158">
                    {[...galleryImages, ...galleryImages].map((img, i) => <button key={i} onClick={() => setCurrentSlide(i % galleryImages.length)} className={`w-8 h-8 flex-shrink-0 overflow-hidden border-2 transition-all duration-300 ${i % galleryImages.length === currentSlide ? 'border-brand-gold scale-110' : 'border-transparent opacity-50 hover:opacity-80'}`} data-id="element-159">
                        <img src={img.src} alt="" className="w-full h-full object-cover" data-id="element-160" />
                      </button>)}
                  </motion.div>
                </div>

                <button onClick={nextSlide} className="w-10 h-10 flex items-center justify-center border border-brand-cream/15 hover:border-brand-gold hover:text-brand-gold transition-colors" data-id="element-161">
                  <ChevronRight className="w-5 h-5" data-id="element-162" />
                </button>
              </div>
            </div>
          </FadeIn>

          {/* Content Side */}
          <div className="max-w-xl" data-id="element-163">
            <FadeIn delay={0.2} data-id="element-164">
              <span className="text-brand-gold tracking-[0.2em] text-sm font-medium uppercase mb-4 block" data-id="element-165">
                {text.label}
              </span>
              <h2 className="font-serif text-4xl md:text-5xl text-brand-wine mb-8" data-id="element-166">
                {text.title}
              </h2>

              <div className="space-y-6 font-sans text-lg text-brand-cream/80 leading-relaxed mb-12" data-id="element-167">
                <p data-id="element-168">{text.p1}</p>
                <p data-id="element-169">{text.p2}</p>
                <p className="font-medium text-brand-cream text-xl border-l-2 border-brand-gold pl-6 py-2 my-8" data-id="element-170">
                  {text.highlight}
                </p>
                <p data-id="element-171">{text.p3}</p>
              </div>
            </FadeIn>

            <FadeIn delay={0.4} data-id="element-172">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-brand-cream/10 pt-8" data-id="element-173">
                <div className="flex flex-col items-center sm:items-start text-center sm:text-left" data-id="element-174">
                  <Sparkles className="w-6 h-6 text-brand-gold mb-3" data-id="element-175" />
                  <h4 className="font-serif text-lg mb-2" data-id="element-176">{text.pillar1}</h4>
                </div>
                <div className="flex flex-col items-center sm:items-start text-center sm:text-left" data-id="element-177">
                  <Crown className="w-6 h-6 text-brand-gold mb-3" data-id="element-178" />
                  <h4 className="font-serif text-lg mb-2" data-id="element-179">{text.pillar2}</h4>
                </div>
                <div className="flex flex-col items-center sm:items-start text-center sm:text-left" data-id="element-180">
                  <Heart className="w-6 h-6 text-brand-gold mb-3" data-id="element-181" />
                  <h4 className="font-serif text-lg mb-2" data-id="element-182">{text.pillar3}</h4>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </div>

      {/* Lightbox Popup */}
      <AnimatePresence data-id="element-183">
        {lightboxIndex !== null && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="fixed inset-0 bg-black/90 z-50" onClick={closeLightbox} data-id="element-184" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} transition={{
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-185">
              <div className="relative max-w-3xl max-h-[85vh] pointer-events-auto" data-id="element-186">
                <button onClick={closeLightbox} className="absolute -top-12 right-0 text-white/70 hover:text-white transition-colors" data-id="element-187">
                  <X className="w-8 h-8" data-id="element-188" />
                </button>

                <button onClick={lightboxPrev} className="absolute left-[-50px] top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors hidden md:block" data-id="element-189">
                  <ChevronLeft className="w-10 h-10" data-id="element-190" />
                </button>

                <AnimatePresence mode="wait" data-id="element-191">
                  <motion.img key={lightboxIndex} src={galleryImages[lightboxIndex].src} alt={galleryImages[lightboxIndex].alt} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.3
              }} className="w-[85vw] h-[85vw] max-w-[85vh] max-h-[85vh] object-cover" data-id="element-192" />
                </AnimatePresence>

                <button onClick={lightboxNext} className="absolute right-[-50px] top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors hidden md:block" data-id="element-193">
                  <ChevronRight className="w-10 h-10" data-id="element-194" />
                </button>

                {/* Mobile nav */}
                <div className="flex justify-center gap-4 mt-4 md:hidden" data-id="element-195">
                  <button onClick={lightboxPrev} className="text-white/70 p-2" data-id="element-196">
                    <ChevronLeft className="w-8 h-8" data-id="element-197" />
                  </button>
                  <button onClick={lightboxNext} className="text-white/70 p-2" data-id="element-198">
                    <ChevronRight className="w-8 h-8" data-id="element-199" />
                  </button>
                </div>

                <div className="text-center mt-3 text-white/40 text-sm font-sans" data-id="element-200">
                  {lightboxIndex + 1} / {galleryImages.length}
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </section>;
}